import React, { useMemo } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from './buttonStyles';
import { AUDIT_OUTCOMES, AuditActionOption } from '../../components/services/auditService';
import {
  ActivityLogFilterValues,
  activityLogFilterSchema,
  defaultDateRange,
  EMPTY_FILTERS,
  fromScopeChoice,
  scopeChoiceOf,
} from './activityLogFilters';
import { ActivityLogTenants } from './useActivityLogTenants';
import { UserDirectory } from './useUserDirectory';
import GroupedPicker, { PickerGroup } from './GroupedPicker';
import { directoryUserLabel, RESELLERS_SECTION, TENANTS_SECTION, userDirectoryGroups } from './userDirectoryGroups';
import { categorizeActions } from './actionCategories';

interface ActivityLogFilterFormProps {
  value: ActivityLogFilterValues;
  /** Globaladmin: adds the Global and All tenants scopes. */
  canUseGlobalScopes: boolean;
  /** Globaladmin and reseller: the Scope dropdown, with the logged-in tenant and the associated tenants. */
  canChooseTenant: boolean;
  /** Shown in brackets after the logged-in tenant's name, e.g. "Reseller". */
  roleLabel: string;
  tenants: ActivityLogTenants;
  /** The Action dropdown: every action by customer-facing name (GET auditlog/actions), shown by category. */
  actionOptions: AuditActionOption[];
  /** The User dropdown: AuthService's user directory for the signed-in user's tenant, loaded as it is opened. */
  users: UserDirectory;
  /** The header's selected tenant: what the tenant scope shows when no tenant was picked here. */
  fallbackTenantId: string | null;
  onApply: (filters: ActivityLogFilterValues) => void;
}

type FormValues = ActivityLogFilterValues & { scopeChoice: string };

// Same height as the buttons so the row lines up; dark colour scheme so the date pickers match the page.
const inputClass =
  'h-[34px] w-full rounded-lg border border-[#474646] bg-[#1a1a1a] px-2.5 text-sm text-white [color-scheme:dark] focus:outline-none focus:border-gray-400';
const labelClass = 'block text-xs text-gray-400 mb-1';

// A typed address can be used as the User filter (a user not in the list, or the list failed to load).
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const typedEmail = (query: string) => (EMAIL_PATTERN.test(query) ? { value: query, label: `Filter by ${query}` } : null);

const FieldError = ({ name }: { name: string }) => <ErrorMessage name={name} render={(msg) => <p className="mt-1 text-xs text-red-400">{msg}</p>} />;

const ActivityLogFilterForm = ({
  value,
  canUseGlobalScopes,
  canChooseTenant,
  roleLabel,
  tenants,
  actionOptions,
  users,
  fallbackTenantId,
  onApply,
}: ActivityLogFilterFormProps) => {
  // Show the API's default range in the date inputs when the URL doesn't set one.
  const initialValues = useMemo<FormValues>(() => {
    const defaults = defaultDateRange();
    return { ...value, from: value.from || defaults.from, to: value.to || defaults.to, scopeChoice: scopeChoiceOf(value, fallbackTenantId) };
  }, [value, fallbackTenantId]);

  // The chosen tenant may not be in the list (still loading, the call failed, or the header's tenant isn't in this
  // user's list): keep it selectable so the dropdown never shows a different tenant from the one being read.
  const currentTenantId = value.scope === 'tenant' ? (value.tenantId || fallbackTenantId || '').toLowerCase() : '';
  const listedIds = useMemo(
    () => new Set([tenants.loggedInTenant?.tenantId, ...tenants.associatedTenants.map((t) => t.tenantId)].filter(Boolean) as string[]),
    [tenants],
  );
  const unlistedCurrentTenant = currentTenantId && !listedIds.has(currentTenantId) ? currentTenantId : null;
  const userGroups = useMemo<PickerGroup[]>(
    () => (users.tree ? userDirectoryGroups(users.tree, users.lists, users.loadUsers) : []),
    [users.tree, users.lists, users.loadUsers],
  );
  const actionGroups = useMemo<PickerGroup[]>(
    () =>
      categorizeActions(actionOptions).map((category) => ({
        key: `category:${category.name}`,
        label: category.name,
        count: category.actions.length,
        items: category.actions.map((option) => ({ value: option.action, label: option.displayName })),
      })),
    [actionOptions],
  );
  // An action from the URL that isn't in the list (or while the list loads) shows as its code.
  const actionNameOf = (code: string) => actionOptions.find((option) => option.action === code)?.displayName || code;

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={activityLogFilterSchema}
      onSubmit={({ scopeChoice, ...values }) =>
        onApply({
          ...values,
          ...(canChooseTenant ? fromScopeChoice(scopeChoice) : { scope: value.scope, tenantId: value.tenantId }),
          actorEmail: values.actorEmail.trim(),
          action: values.action.trim(),
        })
      }
    >
      {({ resetForm }) => (
        <Form aria-label="Activity log filters" className="flex flex-wrap items-start gap-x-3 gap-y-3">
          {canChooseTenant && (
            <div className="w-[280px]">
              <label htmlFor="al-scope" className={labelClass}>
                Scope
              </label>
              <Field as="select" id="al-scope" name="scopeChoice" className={inputClass}>
                {tenants.loggedInTenant && (
                  <option value={`tenant:${tenants.loggedInTenant.tenantId}`}>
                    {tenants.loggedInTenant.tenantName} ({roleLabel})
                  </option>
                )}
                {unlistedCurrentTenant && (
                  <option value={`tenant:${unlistedCurrentTenant}`}>
                    {tenants.loading ? 'Loading tenants…' : tenants.namesById[unlistedCurrentTenant] || 'Selected tenant'}
                  </option>
                )}
                {canUseGlobalScopes && (
                  <>
                    <option value="global">Global (platform)</option>
                    <option value="all">All tenants</option>
                  </>
                )}
                {tenants.associatedTenants.length > 0 && (
                  <optgroup label="Associated tenants">
                    {tenants.associatedTenants.map((tenant) => (
                      <option key={tenant.tenantId} value={`tenant:${tenant.tenantId}`}>
                        {tenant.tenantName}
                      </option>
                    ))}
                  </optgroup>
                )}
              </Field>
            </div>
          )}
          <div className="w-[160px]">
            <label htmlFor="al-from" className={labelClass}>
              From
            </label>
            <Field type="date" id="al-from" name="from" className={inputClass} />
            <FieldError name="from" />
          </div>
          <div className="w-[160px]">
            <label htmlFor="al-to" className={labelClass}>
              To
            </label>
            <Field type="date" id="al-to" name="to" className={inputClass} />
            <FieldError name="to" />
          </div>
          <div className="w-[280px]">
            <label htmlFor="al-email" className={labelClass}>
              User
            </label>
            <Field name="actorEmail">
              {({ field, form }: any) => (
                <GroupedPicker
                  id="al-email"
                  value={field.value}
                  onChange={(email) => form.setFieldValue('actorEmail', email)}
                  allLabel="All users"
                  selectedLabel={directoryUserLabel(field.value, users.lists)}
                  groups={userGroups}
                  onFirstOpen={users.loadTree}
                  status={users.treeStatus === 'ready' ? 'ready' : users.treeStatus === 'failed' ? 'failed' : 'loading'}
                  failedText="Couldn't load the user list. You can still type an email in the search box."
                  onRetry={users.loadTree}
                  searchPlaceholder="Search users or type an email"
                  searchHint="Searches the sections you have opened."
                  customItem={typedEmail}
                  initiallyExpanded={[RESELLERS_SECTION, TENANTS_SECTION]}
                />
              )}
            </Field>
            <FieldError name="actorEmail" />
          </div>
          <div className="w-[260px]">
            <label htmlFor="al-action" className={labelClass}>
              Action
            </label>
            <Field name="action">
              {({ field, form }: any) => (
                <GroupedPicker
                  id="al-action"
                  value={field.value}
                  onChange={(action) => form.setFieldValue('action', action)}
                  allLabel="All actions"
                  selectedLabel={actionNameOf(field.value)}
                  groups={actionGroups}
                  searchPlaceholder="Search actions"
                  panelWidthClass="w-[340px]"
                />
              )}
            </Field>
          </div>
          {/* Entity and Entity key filters are hidden for now and will be brought back later. The API still supports
              them; to restore, uncomment these fields and re-enable the keys in TEXT_KEYS (activityLogFilters.ts).
          <div className="w-[160px]">
            <label htmlFor="al-entity" className={labelClass}>
              Entity
            </label>
            <Field type="text" id="al-entity" name="entity" placeholder="e.g. TenantGroup" className={inputClass} />
            <FieldError name="entity" />
          </div>
          <div className="w-[160px]">
            <label htmlFor="al-entity-key" className={labelClass}>
              Entity key
            </label>
            <Field type="text" id="al-entity-key" name="entityKey" className={inputClass} />
            <FieldError name="entityKey" />
          </div>
          */}
          {/* Category filter hidden for now. To restore, uncomment this field, import AUDIT_CATEGORIES, and re-enable
              'category' in parseFilters / filtersToSearchParams (activityLogFilters.ts).
          <div className="w-[160px]">
            <label htmlFor="al-category" className={labelClass}>
              Category
            </label>
            <Field as="select" id="al-category" name="category" className={inputClass}>
              <option value="">All</option>
              {AUDIT_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Field>
          </div>
          */}
          <div className="w-[160px]">
            <label htmlFor="al-outcome" className={labelClass}>
              Outcome
            </label>
            <Field as="select" id="al-outcome" name="outcome" className={inputClass}>
              <option value="">All</option>
              {AUDIT_OUTCOMES.map((outcome) => (
                <option key={outcome} value={outcome}>
                  {outcome}
                </option>
              ))}
            </Field>
          </div>
          {/* pt-5 = the label's height (text-xs line + mb-1), so the buttons line up with the inputs, not the labels. */}
          <div className="ml-auto flex gap-2 pt-5">
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => {
                // Clearing the URL brings back the API's default range; the scope and tenant stay as chosen.
                resetForm();
                onApply({ ...EMPTY_FILTERS, scope: value.scope, tenantId: value.tenantId });
              }}
            >
              <RotateCcw size={16} />
              <span>Reset</span>
            </button>
            <button type="submit" className={PRIMARY_BUTTON}>
              <SlidersHorizontal size={16} />
              <span>Apply</span>
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ActivityLogFilterForm;
